import { useState, useEffect, useCallback, useRef } from "react";
import { X, Loader2, Sparkles, Check, RotateCw } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { opts } from "./constants";
import { useCerrarConAtras } from "../../utils/useCerrarConAtras";
import { formatPYG } from "../../utils/format";

// CEN 2.0 FASE 2 sub-entrega 3 — Autollenado de precios con IA. Mismo criterio
// que AsignarTareasModal/AsignarNivelesModal: NUNCA pre-seleccionar propuestas
// al cargar — el usuario elige fila por fila o usa "Aceptar alta confianza".
function scoreBadge(status) {
  if (status === "alta") return "bg-emerald-100 text-emerald-700";
  if (status === "media") return "bg-amber-100 text-amber-700";
  if (status === "baja") return "bg-gray-100 text-gray-500";
  return "bg-gray-50 text-gray-400";
}

const PAGE_SIZE = 50;

// Fix AG (2026-09-04): catálogos en los que se puede fijar una marca antes de
// procesar. Las opciones salen de GET /catalogos/marcas (marcas realmente cargadas).
const MARCA_CATALOGOS = [
  { key: "cables",      label: "Cables" },
  { key: "disyuntores", label: "Disyuntores" },
  { key: "tableros",    label: "Tableros" },
  { key: "mecanismos",  label: "Mecanismos" },
  { key: "materiales",  label: "Listas de precios" },
];
const marcaLabel = (key) => (MARCA_CATALOGOS.find(c => c.key === key) || { label: key }).label;

export function AutollenadoIAModal({ open, budgetId, onClose, onApplied, aprobado = false }) {
  const [step, setStep] = useState("inicio"); // inicio | progreso | revision
  const [starting, setStarting] = useState(false);
  const [job, setJob] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState("todas");
  // Fix AZ (2026-09-06): "permitir la selección por separado de material y mano
  // de obra" (Edson, 06/09). Cada ítem marcado guarda qué lado se va a aplicar:
  // { item_uid: { mat: true, mo: false } }.
  const [selected, setSelected] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [modelInfo, setModelInfo] = useState(null);
  // Fix U (2026-09-01): en presupuestos aprobados arranca marcado para no tocar precios.
  const [soloVincular, setSoloVincular] = useState(!!aprobado);
  // Fix AG (2026-09-04): marca elegida por catálogo ("" = todas) y marcas disponibles.
  const [marcas, setMarcas] = useState({});
  const [marcasDisponibles, setMarcasDisponibles] = useState({});
  // Fix AL (2026-09-04): factor de metraje de las bocas para esta obra.
  const [metraje, setMetraje] = useState("");
  // Fix AZ (2026-09-06): aviso antes de perder la selección, y multiplicador de
  // metraje que se está escribiendo en cada fila.
  const [confirmarCierre, setConfirmarCierre] = useState(false);
  const [editandoMetraje, setEditandoMetraje] = useState({});
  const pollRef = useRef(null);

  const seleccionados = Object.keys(selected).filter(u => selected[u] && (selected[u].mat || selected[u].mo));

  useEffect(() => {
    if (!open) return;
    setStep("inicio"); setJob(null); setSelected({}); setPage(1); setFilterStatus("todas");
    setConfirmarCierre(false); setEditandoMetraje({});
    // Fix U (2026-09-01): recalcula el default cada vez que se abre (antes solo
    // corría al montar) — así no queda un toggle manual pegado entre reaperturas
    // o al cambiar de presupuesto.
    setSoloVincular(!!aprobado);
    axios.get(`${API}/settings/autollenado-model`, opts).then(({ data }) => setModelInfo(data)).catch(() => {});
    // Fix AG (2026-09-04): cada apertura arranca con "Todas las marcas".
    setMarcas({});
    setMetraje("");
    // Fix CC (2026-09-10): las marcas de la copia de ESTE presupuesto, que es con la que cotiza el job.
    axios.get(budgetId ? `${API}/presupuestos/${budgetId}/catalogos/marcas` : `${API}/catalogos/marcas`, opts)
      .then(({ data }) => setMarcasDisponibles(data || {})).catch(() => setMarcasDisponibles({}));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, budgetId, aprobado]);

  const pollJob = useCallback((jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/budgets/${budgetId}/autollenado/jobs/${jobId}`, opts);
        setJob(data);
        if (data.status === "completado" || data.status === "error" || data.status === "cancelado") {
          clearInterval(pollRef.current);
          if (data.status === "completado") { setStep("revision"); loadPropuestas(jobId, 1, "todas"); }
          if (data.status === "error") toast.error(`El autollenado falló: ${data.error || "error desconocido"}`);
        }
      } catch { clearInterval(pollRef.current); }
    }, 2000);
  }, [budgetId]);

  const iniciar = async (soloSinPrecio) => {
    setStarting(true);
    try {
      // Fix AG (2026-09-04): solo los catálogos con una marca elegida.
      const marcasElegidas = Object.fromEntries(Object.entries(marcas).filter(([, v]) => v));
      // Fix AL (2026-09-04): factor de metraje (vacío o 1 = el del catálogo).
      const factor = parseFloat(String(metraje).replace(",", "."));
      const { data } = await axios.post(`${API}/budgets/${budgetId}/autollenado/iniciar`, {
        solo_sin_precio: soloSinPrecio, marcas: marcasElegidas,
        metraje_factor: Number.isFinite(factor) && factor > 0 ? factor : null,
      }, opts);
      setStep("progreso");
      setJob({ status: "pendiente", total_items: data.total_items, batches_done: 0, batches_total: 0, processed_items: 0 });
      pollJob(data.job_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar el autollenado");
    } finally { setStarting(false); }
  };

  const loadPropuestas = async (jobId, p, status) => {
    try {
      const { data } = await axios.get(`${API}/budgets/${budgetId}/autollenado/jobs/${jobId}/propuestas`, {
        ...opts, params: { match_status: status, page: p, page_size: PAGE_SIZE },
      });
      setProposals(data.items || []); setTotal(data.total || 0); setPage(p);
    } catch { toast.error("Error al cargar las propuestas"); }
  };

  useEffect(() => {
    if (step === "revision" && job?.id) loadPropuestas(job.id, 1, filterStatus);
  }, [filterStatus]); // eslint-disable-line

  // Fix AZ (2026-09-06): qué lados tiene para ofrecer cada propuesta.
  const tieneMat = (p) => !!p.precio_sugerido_mat;
  const tieneMo = (p) => !!p.precio_sugerido_mo;
  const marcado = (p) => selected[p.item_uid] || { mat: false, mo: false };
  const filaMarcada = (p) => { const s = marcado(p); return s.mat || s.mo; };

  // Fix BG (2026-09-08): "el tema de los items repetidos al cuál les damos ok
  // varias veces se repite también en cables - apruebo los 3x4 como 3 veces en
  // este screem" (Gerencia Técnica, 08/09). El Fix BA agrupa por descripción
  // idéntica, que se sabe antes de consultar al modelo; pero varios alimentadores
  // con descripciones distintas terminan en la MISMA referencia y el mismo precio.
  // Acá se agrupan por la propuesta, que es lo que la persona está aprobando.
  // Las que no tienen coincidencia y las ya aplicadas van siempre por separado.
  const firmaPropuesta = (p) => [p.catalogo_id || "", p.mo_id || "", p.precio_sugerido_mat || 0,
                                 p.precio_sugerido_mo || 0, p.metraje_factor || ""].join("|");

  const filasAgrupadas = [];
  const _porFirma = {};
  proposals.forEach(p => {
    const fila = { ...p, _grupo: [p.item_uid], _repetidos: p.repetidos || 1, _descripciones: [p.descripcion] };
    if (!p.catalogo_id || p.applied) { filasAgrupadas.push(fila); return; }
    const k = firmaPropuesta(p);
    const ya = _porFirma[k];
    if (ya) {
      ya._grupo.push(p.item_uid);
      ya._repetidos += (p.repetidos || 1);
      ya._descripciones.push(p.descripcion);
      return;
    }
    _porFirma[k] = fila;
    filasAgrupadas.push(fila);
  });

  const togglePartes = (p, parte) => {
    setSelected(prev => {
      const actual = prev[p.item_uid] || { mat: false, mo: false };
      const next = { ...actual, [parte]: !actual[parte] };
      const out = { ...prev };
      // Fix BG (2026-09-08): lo que se aprueba es la propuesta, así que vale para
      // todos los ítems que la comparten.
      (p._grupo || [p.item_uid]).forEach(uid => {
        if (!next.mat && !next.mo) delete out[uid]; else out[uid] = { ...next };
      });
      return out;
    });
  };

  const toggleFila = (p) => {
    setSelected(prev => {
      const out = { ...prev };
      const uids = p._grupo || [p.item_uid];
      if (out[p.item_uid]) { uids.forEach(uid => delete out[uid]); return out; }
      uids.forEach(uid => { out[uid] = { mat: tieneMat(p), mo: tieneMo(p) }; });
      return out;
    });
  };

  const marcarVarias = (filas, etiqueta) => {
    const aplicables = filas.filter(p => !p.applied && p.catalogo_id && (tieneMat(p) || tieneMo(p)));
    setSelected(prev => {
      const out = { ...prev };
      // Fix BG (2026-09-08): cada fila puede representar a varios ítems.
      aplicables.forEach(p => (p._grupo || [p.item_uid]).forEach(uid => { out[uid] = { mat: tieneMat(p), mo: tieneMo(p) }; }));
      return out;
    });
    const nItems = aplicables.reduce((s, p) => s + (p._repetidos || 1), 0);
    toast.success(aplicables.length > 0 ? `${nItems} ${etiqueta}` : "No hay propuestas pendientes en esta página");
  };

  // Fix AZ (2026-09-06): "se puede habilitar un botón 'seleccionar todo' en cada
  // pagina?" (Edson, 06/09).
  const seleccionarTodo = () => marcarVarias(filasAgrupadas, "ítem(s) de esta página marcado(s) — revisá y confirmá");
  const acceptHighConfidence = () => marcarVarias(filasAgrupadas.filter(p => p.match_status === "alta"), "ítem(s) de alta confianza marcado(s) — revisá y confirmá");
  const limpiarSeleccion = () => setSelected({});

  // Fix AZ (2026-09-06): "si no hubiere buen match o fuese menos del 50% el score,
  // permitir también la modificación manual del multiplicador de metraje". Solo
  // recalcula esta propuesta; el presupuesto no se toca hasta aplicar.
  const guardarMetraje = async (p) => {
    const crudo = editandoMetraje[p.item_uid];
    const limpiar = () => setEditandoMetraje(prev => { const o = { ...prev }; delete o[p.item_uid]; return o; });
    if (crudo === undefined) return;
    const f = parseFloat(String(crudo).replace(",", "."));
    const factor = Number.isFinite(f) && f > 0 ? f : null;
    if ((p.metraje_factor || null) === factor) { limpiar(); return; }
    try {
      // Fix BG (2026-09-08): la fila puede representar a varios ítems con la misma
      // propuesta; el metraje corregido vale para todos ellos.
      const uids = p._grupo || [p.item_uid];
      let ultimo = null;
      for (const uid of uids) {
        const { data } = await axios.put(`${API}/budgets/${budgetId}/autollenado/jobs/${job.id}/metraje`, { item_uid: uid, factor }, opts);
        ultimo = data;
        setProposals(prev => prev.map(x => (x.item_uid === uid ? { ...x, ...data } : x)));
      }
      if (!ultimo) return;
      limpiar();
      toast.success(factor ? `Metraje ×${factor} aplicado a ${uids.length} ítem(s)` : "Metraje del catálogo restablecido");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo recalcular con ese metraje");
    }
  };

  const confirmar = async () => {
    if (seleccionados.length === 0) { toast.info("Seleccioná al menos un ítem para aplicar"); return; }
    setConfirming(true);
    try {
      // Fix AZ (2026-09-06): junto con los ítems viaja qué lado se aplica de cada uno.
      const partes = Object.fromEntries(seleccionados.map(u => [u, ["mat", "mo"].filter(k => selected[u][k])]));
      const { data } = await axios.post(`${API}/budgets/${budgetId}/autollenado/jobs/${job.id}/confirmar`, { item_uids: seleccionados, partes, solo_vincular: soloVincular }, opts);
      toast.success(soloVincular ? `${data.applied} ítem(s) vinculado(s) al catálogo, precios sin cambios` : `${data.applied} ítem(s) actualizado(s) con precio`);
      onApplied?.();
      setSelected({});
      loadPropuestas(job.id, page, filterStatus);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al aplicar las propuestas");
    } finally { setConfirming(false); }
  };

  // Fix AZ (2026-09-06): "generar una advertencia al darle click accidental fuera
  // del cuadro… recién le di click fuera sin querer y me sacó de todos los items
  // que ya seleccioné" (Edson, 06/09).
  const intentarCerrar = () => {
    if (step === "revision" && seleccionados.length > 0) { setConfirmarCierre(true); return; }
    onClose();
  };

  // Fix AI (2026-09-04): el botón "atrás" del navegador cierra este modal.
  useCerrarConAtras(open, onClose);
  if (!open) return null;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pct = job?.total_items ? Math.round((job.processed_items / job.total_items) * 100) : 0;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="autollenado-ia-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={intentarCerrar} data-testid="autollenado-backdrop" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Sparkles size={16} className="text-violet-500" /> Autollenado de precios con IA
            </h3>
            {modelInfo && <p className="text-xs text-gray-500 mt-0.5">Modelo actual: {modelInfo.actual.model}</p>}
          </div>
          <button onClick={intentarCerrar} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="autollenado-close-btn"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-4 flex-1 overflow-y-auto">
          {step === "inicio" && (
            <>
              <p className="text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
                Busca en los catálogos (bocas, mano de obra, listas de precios, cables, disyuntores, tableros, mecanismos y
                materiales varios) qué referencias componen cada ítem sin precio de material o de mano de obra. Los
                alimentadores "con cable ..." se componen por la especificación del cable (4x25+16T, 2x(3x1x95)+50T...).
                Nada se aplica automáticamente — vas a poder revisar y confirmar item por item antes de guardar.
                {/* Fix BA (2026-09-07): "validar una sola vez y replicar en
                    absolutamente TODOS los items iguales" (Edson, 06/09). */}
                {" "}Los ítems que tienen la misma descripción se consultan una sola vez y se revisan juntos: lo que confirmes se aplica a todos ellos.
              </p>
              {/* Fix AG (2026-09-04): marca por catálogo. Con una marca elegida, la IA solo ve esa marca. */}
              <div className="border border-gray-200 rounded-xl p-3" data-testid="autollenado-marcas">
                <p className="text-[10px] text-gray-500 uppercase font-semibold tracking-wide mb-2">Marca por catálogo (opcional)</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {MARCA_CATALOGOS.filter(c => (marcasDisponibles[c.key] || []).length > 0).map(c => (
                    <label key={c.key} className="flex flex-col gap-1 text-xs text-gray-600">
                      <span className="font-semibold">{c.label}</span>
                      <select value={marcas[c.key] || ""} onChange={e => setMarcas(prev => ({ ...prev, [c.key]: e.target.value }))}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid={`autollenado-marca-${c.key}`}>
                        <option value="">Todas las marcas</option>
                        {(marcasDisponibles[c.key] || []).map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-2">
                  Si elegís una marca, los ítems que no tengan referencia de esa marca quedan "sin coincidencia" en vez de completarse con otra marca.
                </p>
              </div>
              {/* Fix AL (2026-09-04): metraje de las bocas de esta obra. */}
              <div className="border border-gray-200 rounded-xl p-3" data-testid="autollenado-metraje">
                <p className="text-[10px] text-gray-500 uppercase font-semibold tracking-wide mb-2">Metraje de las bocas (opcional)</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-gray-600">Los caños y cables de cada boca son</span>
                  <input value={metraje} onChange={e => setMetraje(e.target.value)} inputMode="decimal" placeholder="1"
                    className="w-20 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-right" data-testid="autollenado-metraje-input" />
                  <span className="text-xs text-gray-600">veces los del catálogo.</span>
                  {[1.25, 1.5, 1.75, 2].map(f => (
                    <button key={f} type="button" onClick={() => setMetraje(String(f))}
                      className="text-[11px] px-2 py-1 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50" data-testid={`autollenado-metraje-${f}`}>
                      ×{f}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-2">
                  Si en el catálogo una boca lleva 2 m de caño y en esta obra lleva 3,5 m, poné 1,75: los caños y cables se
                  multiplican por eso y las cajas, llaves y abrazaderas quedan igual. El precio de cada boca se recalcula y
                  el consumo de la obra se mide contra esos metros. Vacío o 1 = el metraje del catálogo.
                </p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => iniciar(true)} disabled={starting}
                  className="flex-1 flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-60"
                  data-testid="autollenado-iniciar-sin-precio-btn">
                  {starting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Procesar ítems sin precio
                </button>
                <button onClick={() => iniciar(false)} disabled={starting}
                  className="flex-1 flex items-center justify-center gap-2 border border-violet-300 text-violet-700 hover:bg-violet-50 rounded-xl py-3 text-sm font-semibold disabled:opacity-60"
                  data-testid="autollenado-iniciar-todos-btn">
                  <RotateCw size={15} /> Reprocesar todos los ítems
                </button>
              </div>
              {modelInfo && (
                <div className="flex items-center gap-2 text-xs pt-2 border-t border-gray-100">
                  <span className="text-gray-400">Modelo de IA a usar:</span>
                  <select value={`${modelInfo.actual.provider}::${modelInfo.actual.model}`}
                    onChange={async (e) => {
                      const [provider, model] = e.target.value.split("::");
                      try {
                        await axios.put(`${API}/settings/autollenado-model`, { provider, model }, opts);
                        setModelInfo(prev => ({ ...prev, actual: { provider, model } }));
                        toast.success("Modelo actualizado");
                      } catch (err) { toast.error(err.response?.data?.detail || "Sin permisos para cambiar el modelo"); }
                    }}
                    className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white" data-testid="autollenado-model-select">
                    {modelInfo.opciones.map(o => <option key={o.model} value={`${o.provider}::${o.model}`}>{o.label}</option>)}
                  </select>
                </div>
              )}
            </>
          )}

          {step === "progreso" && (
            <div className="py-10 text-center space-y-3" data-testid="autollenado-progreso">
              <Loader2 size={32} className="animate-spin text-violet-500 mx-auto" />
              <p className="text-sm font-semibold text-gray-700">Procesando ítems con IA…</p>
              {/* Fix BA (2026-09-07): se procesa una vez cada descripción distinta. */}
              <p className="text-xs text-gray-500">{job?.processed_items || 0} / {job?.total_items || 0} descripciones · lote {job?.batches_done || 0} de {job?.batches_total || "…"}</p>
              {job?.total_lineas > (job?.total_items || 0) && (
                <p className="text-xs text-gray-400">Cubren {job.total_lineas} ítems del presupuesto: los que se repiten se consultan una sola vez.</p>
              )}
              <div className="w-full max-w-sm mx-auto bg-gray-100 rounded-full h-2 mx-auto">
                <div className="bg-violet-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          )}

          {step === "revision" && (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid="autollenado-filter">
                  <option value="todas">Todas</option>
                  <option value="alta">Alta confianza (≥85)</option>
                  <option value="media">Media confianza</option>
                  <option value="baja">Baja confianza</option>
                  <option value="sin_match">Sin coincidencia</option>
                </select>
                {/* Fix BA (2026-09-07): lo que se revisa son descripciones distintas;
                    cada una cubre todos los ítems iguales del presupuesto. */}
                <span className="text-xs text-gray-400">
                  {total} descripción(es){job?.total_lineas > total ? ` · ${job.total_lineas} ítems del presupuesto` : ""}
                  {job?.counts ? ` · alta=${job.counts.alta} media=${job.counts.media} baja=${job.counts.baja} sin_match=${job.counts.sin_match}` : ""}
                </span>
                {Object.keys(job?.marcas || {}).length > 0 && (
                  <span className="text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5" data-testid="autollenado-marcas-usadas">
                    Solo marca: {Object.entries(job.marcas).map(([k, v]) => `${marcaLabel(k)} ${v}`).join(" · ")}
                  </span>
                )}
                {/* Fix AL (2026-09-04): metraje con el que se calcularon las bocas. */}
                {job?.metraje_factor && (
                  <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="autollenado-metraje-usado">
                    Metraje de bocas ×{job.metraje_factor}
                  </span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  {/* Fix AZ (2026-09-06): "se puede habilitar un botón 'seleccionar
                      todo' en cada pagina?" (Edson, 06/09). */}
                  <button onClick={seleccionarTodo}
                    className="flex items-center gap-1.5 border border-violet-300 text-violet-700 hover:bg-violet-50 px-3 py-1.5 rounded-lg text-xs font-semibold"
                    data-testid="autollenado-select-all-btn">
                    <Check size={13} /> Seleccionar todo de esta página
                  </button>
                  {seleccionados.length > 0 && (
                    <button onClick={limpiarSeleccion} className="text-xs text-gray-500 hover:text-gray-700 underline"
                      data-testid="autollenado-clear-btn">Limpiar ({seleccionados.length})</button>
                  )}
                  <button onClick={acceptHighConfidence}
                    className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold"
                    data-testid="autollenado-accept-high-btn">
                    <Check size={13} /> Marcar alta confianza
                  </button>
                </div>
              </div>
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-[#EBF3FB]">
                    <tr>
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide w-8"></th>
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">Ítem</th>
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">Referencia sugerida</th>
                      {/* Fix AZ (2026-09-06): con qué metraje se cotizó la boca, y el
                          multiplicador a mano cuando el ítem no lo declara. */}
                      <th className="text-center px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">Metraje</th>
                      <th className="text-right px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">Precio sugerido</th>
                      <th className="text-center px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filasAgrupadas.map(p => (
                      <tr key={p.item_uid} className={`border-t border-gray-100 ${p.applied ? "bg-emerald-50/50" : ""}`} data-testid={`autollenado-row-${p.item_uid}`}>
                        <td className="px-3 py-2">
                          {p.applied ? (
                            <span className="text-emerald-600" title="Ya aplicado"><Check size={14} /></span>
                          ) : (
                            <input type="checkbox" checked={filaMarcada(p)} onChange={() => toggleFila(p)}
                              disabled={!p.catalogo_id} data-testid={`autollenado-checkbox-${p.item_uid}`} />
                          )}
                        </td>
                        {/* Fix AI (2026-09-04): descripción completa, en varias líneas. */}
                        <td className="px-3 py-2 font-semibold text-gray-800 max-w-[200px] whitespace-normal break-words align-top" title={p.descripcion}>
                          {p.numero} — {p.descripcion}
                          {/* Fix BA (2026-09-07): "validar una sola vez y replicar en
                              absolutamente TODOS los items iguales" (Edson, 06/09). */}
                          {p._repetidos > 1 && (
                            <span className="ml-1.5 inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[#EBF3FB] text-[#1A4B7A] align-middle"
                              title={`${p._repetidos} ítem(s) del presupuesto con esta misma propuesta. Se aplica a todos.\n\n${(p._descripciones || []).slice(0, 12).join("\n")}`}
                              data-testid={`autollenado-repetidos-${p.item_uid}`}>×{p._repetidos} ítems</span>
                          )}
                          {/* Fix BG (2026-09-08): cuando la fila junta descripciones
                              distintas con la misma propuesta, se listan debajo para
                              que se vea qué se está aprobando. */}
                          {(p._descripciones || []).length > 1 && (
                            <p className="text-[9px] text-gray-400 font-normal mt-0.5" data-testid={`autollenado-agrupadas-${p.item_uid}`}>
                              + {(p._descripciones || []).length - 1} descripción(es) más con la misma propuesta
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-600 max-w-[220px] whitespace-normal break-words" title={p.catalogo_descripcion}>{p.catalogo_descripcion || <span className="text-gray-300">sin coincidencia</span>}</td>
                        {/* Fix AZ (2026-09-06): metraje de esta boca. Sale de los
                            metros que el cómputo escribe en la descripción; se puede
                            corregir a mano y la propuesta se recalcula sola. */}
                        <td className="px-3 py-2 text-center align-top whitespace-nowrap">
                          {p.catalogo_tipo === "boca" ? (
                            <div className="flex flex-col items-center gap-0.5">
                              <div className="flex items-center gap-1">
                                <span className="text-gray-400">×</span>
                                <input
                                  value={editandoMetraje[p.item_uid] !== undefined ? editandoMetraje[p.item_uid] : (p.metraje_factor || "")}
                                  onChange={e => setEditandoMetraje(prev => ({ ...prev, [p.item_uid]: e.target.value }))}
                                  onBlur={() => guardarMetraje(p)} inputMode="decimal" placeholder="1" disabled={p.applied}
                                  className="w-14 border border-gray-200 rounded px-1 py-0.5 text-[11px] text-right disabled:bg-gray-50"
                                  data-testid={`autollenado-metraje-fila-${p.item_uid}`} />
                              </div>
                              {p.metraje_item ? (
                                <span className="text-[10px] text-gray-400">{p.metraje_item} m · catálogo {p.metraje_catalogo} m</span>
                              ) : (
                                <span className="text-[10px] text-gray-300">sin metros en la descripción</span>
                              )}
                            </div>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        {/* Fix AZ (2026-09-06): "permitir la selección por separado de
                            material y mano de obra" (Edson, 06/09). La casilla de la
                            izquierda sigue marcando los dos lados de una vez. */}
                        <td className="px-3 py-2 text-right font-mono align-top">
                          <div className="flex flex-col items-end gap-0.5">
                            {tieneMat(p) && (
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <span className="text-gray-600">Mat: {formatPYG(p.precio_sugerido_mat)}</span>
                                <input type="checkbox" checked={marcado(p).mat} disabled={p.applied || !p.catalogo_id}
                                  onChange={() => togglePartes(p, "mat")} data-testid={`autollenado-check-mat-${p.item_uid}`} />
                              </label>
                            )}
                            {tieneMo(p) && (
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <span className="text-gray-600">MO: {formatPYG(p.precio_sugerido_mo)}</span>
                                <input type="checkbox" checked={marcado(p).mo} disabled={p.applied || !p.catalogo_id}
                                  onChange={() => togglePartes(p, "mo")} data-testid={`autollenado-check-mo-${p.item_uid}`} />
                              </label>
                            )}
                            {!tieneMat(p) && !tieneMo(p) && <span className="text-gray-300">—</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          {p.applied ? (
                            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">Aplicado</span>
                          ) : (
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${scoreBadge(p.match_status)}`}>{Math.round(p.score)}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filasAgrupadas.length === 0 && (
                      <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">Sin resultados para este filtro.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 text-xs text-gray-500">
                  <button disabled={page === 1} onClick={() => loadPropuestas(job.id, page - 1, filterStatus)} className="px-2 py-1 border rounded disabled:opacity-40">Anterior</button>
                  Página {page} de {totalPages}
                  <button disabled={page >= totalPages} onClick={() => loadPropuestas(job.id, page + 1, filterStatus)} className="px-2 py-1 border rounded disabled:opacity-40">Siguiente</button>
                </div>
              )}
            </>
          )}
        </div>

        {step === "revision" && (
          <div className="px-6 pb-6 pt-2 flex flex-col gap-2 flex-shrink-0">
            {/* Fix U (2026-09-01): vincular sin tocar precios (obligatorio en aprobados) */}
            <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
              <input type="checkbox" checked={soloVincular} onChange={e => setSoloVincular(e.target.checked)}
                className="rounded border-gray-300" data-testid="autollenado-solo-vincular" />
              Solo vincular al catálogo (no cambiar precios ni totales)
              {aprobado && <span className="text-amber-700 font-medium">— presupuesto aprobado: recomendado</span>}
            </label>
          <div className="flex gap-3">
            <button onClick={intentarCerrar} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cerrar sin aplicar</button>
            <button onClick={confirmar} disabled={confirming || seleccionados.length === 0}
              className="flex-1 flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
              data-testid="autollenado-confirmar-btn">
              {confirming ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              {confirming ? "Aplicando..." : `Aplicar ${seleccionados.length} seleccionado(s)`}
            </button>
          </div>
          </div>
        )}
      </div>

      {/* Fix AZ (2026-09-06): "generar una advertencia al darle click accidental
          fuera del cuadro" (Edson, 06/09). El análisis queda guardado igual: lo
          que se pierde es la revisión hecha a mano. */}
      {confirmarCierre && (
        <div className="absolute inset-0 flex items-center justify-center p-4" data-testid="autollenado-confirmar-cierre">
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-3">
            <h4 className="text-sm font-bold text-gray-900">Tenés {seleccionados.length} ítem(s) marcados</h4>
            <p className="text-xs text-gray-600">
              Si cerrás ahora se pierde lo que marcaste y hay que volver a revisarlo. Las propuestas del análisis quedan guardadas.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmarCierre(false)}
                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-xl py-2 text-xs font-semibold"
                data-testid="autollenado-seguir-btn">Seguir revisando</button>
              <button onClick={() => { setConfirmarCierre(false); onClose(); }}
                className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2 text-xs font-medium hover:bg-gray-50"
                data-testid="autollenado-cerrar-igual-btn">Cerrar igual</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
